<script setup>
import { useImage } from "#imports";
import {
  getApplicableBreakpoints
} from "@voix/composables/useBreakpoints";
import { useWindowSize } from "@vueuse/core";
import { computed, inject } from "vue";
const props = defineProps({
  field: { type: Object, required: false },
  breakpoint: { type: String, required: true },
  background: { type: Boolean, required: false },
  loading: { type: String, required: false },
  debug: { type: Boolean, required: true }
});
const emit = defineEmits([
  "load"
]);
const nuxtImage = useImage();
const { width: windowWidth } = useWindowSize();
function getBreakpointConfig(breakpoint) {
  if (props.field === null)
    return null;
  const mediaField = props.field;
  if (mediaField.config?.breakpoints) {
    const breakpointConfig = mediaField.config.breakpoints[breakpoint];
    if (breakpointConfig)
      return breakpointConfig;
    const applicableBreakpoints = getApplicableBreakpoints(
      windowWidth.value
    ).reverse();
    for (const applicableBreakpoint of applicableBreakpoints) {
      if (mediaField.config?.breakpoints[applicableBreakpoint.name]) {
        return mediaField.config?.breakpoints[applicableBreakpoint.name];
      }
    }
  }
  return null;
}
function getBreakpointValue(breakpoint) {
  if (props.field === null)
    return null;
  const mediaField = props.field;
  if (mediaField.value && Array.isArray(mediaField.value)) {
    const breakpointValue = mediaField.value.find(
      (b) => b.breakpoint === breakpoint
    );
    if (breakpointValue)
      return breakpointValue;
    const applicableBreakpoints = getApplicableBreakpoints(
      windowWidth.value
    ).reverse();
    for (const applicableBreakpoint of applicableBreakpoints) {
      const applicableBreakpointMedia = mediaField.value.find(
        (b) => b.breakpoint === applicableBreakpoint.name
      );
      if (applicableBreakpointMedia)
        return applicableBreakpointMedia;
    }
  }
  return null;
}
function generateMediaSrcSetUrl(mediaBreakpoint, breakpointConfig) {
  if (typeof mediaBreakpoint !== "undefined" && typeof breakpointConfig !== "undefined" && typeof mediaBreakpoint === "object") {
    let json = mediaBreakpoint.modifiers;
    if (typeof json !== "string")
      json = JSON.stringify(json);
    const modifiers = JSON.parse(json);
    modifiers.w = breakpointConfig.width;
    modifiers.h = breakpointConfig.height;
    return nuxtImage(mediaBreakpoint.url, modifiers, { provider: mediaBreakpoint.provider });
  }
  return "";
}
const breakpointImageSource = computed(() => {
  if (props.field === null && props.breakpoint)
    return null;
  const media = getBreakpointValue(props.breakpoint);
  const config = getBreakpointConfig(props.breakpoint);
  if (media === null || config === null)
    return null;
  return generateMediaSrcSetUrl(media, config);
});
const parentIsFirstElement = inject("parentIsFirstElement", false);
const currentMedia = computed(() => {
  if (props.breakpoint === void 0)
    return null;
  return getBreakpointValue(props.breakpoint);
});
const currentConfig = computed(() => {
  if (props.breakpoint === void 0)
    return null;
  return getBreakpointConfig(props.breakpoint);
});
const currentModifiers = computed(() => {
  if (currentMedia?.value?.modifiers) {
    let json = currentMedia?.value?.modifiers;
    if (typeof json !== "string")
      json = JSON.stringify(json);
    return json;
  }
  return {};
});
const renderFormat = computed(() => {
  const media = currentMedia.value;
  if (media && media.type && media?.type === "video")
    return "Video";
  return "Image";
});
const intrensicStyles = computed(() => {
  if (currentMedia.value === null)
    return {};
  if (currentMedia.value.modifiers.fit !== "contain")
    return {};
  if (currentMedia.value.modifiers.w >= currentMedia.value.modifiers.h) {
    return {
      height: `${currentMedia.value.modifiers.h}px`
    };
  } else {
    return {
      width: `${currentMedia.value.modifiers.w}px`
    };
  }
});
const backgroundClasses = computed(() => {
  if (props.background === false || currentMedia.value === null)
    return [];
  const classes = [];
  let json = currentMedia?.value?.modifiers;
  if (typeof json !== "string")
    json = JSON.stringify(json);
  const modifiers = JSON.parse(json);
  if (modifiers.position)
    classes.push(`bg-${modifiers.position}`);
  if (modifiers.fit) {
    const fit = modifiers.fit === "none" ? "auto" : "modifiers.fit";
    classes.push(`bg-${fit}`);
  }
  return classes;
});
function mediaLoaded() {
  emit("load", { src: breakpointImageSource, currentMedia: currentMedia.value });
}
</script>

<template>
  <template v-if="field && breakpointImageSource">
    <template v-if="!background">
      <picture
        v-bind="$attrs"
        class="voix-media"
      >
        <img
          :src="breakpointImageSource"
          v-bind="$attrs"
          :alt="currentMedia?.title"
          :loading="parentIsFirstElement ? 'eager' : loading"
          :fetchpriority="parentIsFirstElement ? 'high' : 'auto'"
          :class="backgroundClasses"
          :style="intrensicStyles"
          @load="mediaLoaded"
        >
      </picture>
    </template>
    <template v-else>
      <slot
        :image="breakpointImageSource"
        v-bind="$attrs"
        :loading="parentIsFirstElement ? 'eager' : loading"
        :background-classes="backgroundClasses"
        :style="[intrensicStyles, { backgroundImage: `url(${breakpointImageSource})` }]"
        :fetchpriority="parentIsFirstElement ? 'high' : 'auto'"
        :alt="currentMedia?.title"
        :class="backgroundClasses"
        :modifiers="currentModifiers"
      />
    </template>
  </template>

  <div
    v-if="debug"
    class="space-y-4 mt-[25px] bg-white p-8 px-8 rounded-sm whitespace-pre-line break-words max-w-[100vw] overflow-x-scroll"
  >
    <ClientOnly>
      <h1 class="text-2xl">
        Media Field Debug
      </h1>
      <div>
        <strong>Breakpoint Source:</strong>
        <pre>{{ breakpointImageSource }}</pre>
      </div>
      <div>
        <strong>Media to use:</strong>
        <pre>{{ currentMedia }}</pre>
      </div>
      <div>
        <strong>Config To Use:</strong>
        <pre>{{ currentConfig }}</pre>
      </div>
      <div>
        <strong>Render Format:</strong>
        <pre>{{ renderFormat }}</pre>
      </div>
      <div>
        <strong>Background:</strong>
        <pre>{{ background }}</pre>
      </div>
      <div>
        <strong>Background Classes:</strong>
        <pre>{{ backgroundClasses }}</pre>
      </div>
      <div>
        <strong>Breakpoint:</strong>
        <pre>{{ breakpoint || "Not defined" }}</pre>
      </div>
      <div>
        <strong>Parent is first element:</strong>
        <pre>{{ parentIsFirstElement }}</pre>
      </div>
    </ClientOnly>
  </div>
</template>
